import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, googleProvider, signInWithPopup, signOut, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const actionCodeSettings = {
  url: process.env.REACT_APP_URL || 'http://localhost:3000',
  handleCodeInApp: true,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Auto sign out after 2 hours of inactivity
  useEffect(() => {
    let inactivityTimer;
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        if (auth.currentUser) {
          signOut(auth);
        }
      }, 15 * 60 * 1000); // 15 minutes
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken(true); // force refresh
        setToken(idToken);
        setUser(firebaseUser);
        sessionStorage.setItem('wasLoggedIn', 'true');

        // Create user record in our database
        try {
          const meRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          const meData = await meRes.json();
          if (meData.user) setDbUser(meData.user);
        } catch (err) {
          console.error('User sync error:', err);
        }

        // Claim any guest session
        try {
          const sessionToken = localStorage.getItem('pinsite_session');
          if (sessionToken) {
            await fetch(`${process.env.REACT_APP_API_URL}/api/designs/claim-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`
              },
              body: JSON.stringify({ sessionToken })
            });
          }
        } catch (err) {
          console.error('Claim session error:', err);
        }

      } else {
        setUser(null);
        setToken(null);
        setDbUser(null);
        // Redirect if user was logged in during this session (manual or timeout)
        const pendingOrderId = sessionStorage.getItem('pendingOrderDesignId');
        if (pendingOrderId) {
          sessionStorage.removeItem('pendingOrderDesignId');
          window.location.href = `/order/${pendingOrderId}`;
          return;
        }
        if (window.location.pathname !== '/' && sessionStorage.getItem('wasLoggedIn')) {
          sessionStorage.removeItem('wasLoggedIn');
          window.location.href = '/';
        }
      }
      setLoading(false);
    });

    // Handle email link sign in
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => {
            window.localStorage.removeItem('emailForSignIn');
          })
          .catch((err) => console.error('Email link sign in error:', err));
      }
    }

    // Refresh token every 50 minutes to prevent expiry
    const refreshInterval = setInterval(async () => {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (err) {
          console.error('Token refresh error:', err);
        }
      }
    }, 50 * 60 * 1000);

    return () => { unsubscribe(); clearInterval(refreshInterval); };
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Google sign in error:', err);
      throw err;
    }
  };

  const sendEmailLink = async (email) => {
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
    } catch (err) {
      console.error('Send email link error:', err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      window._manualLogout = true;
      await signOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
      throw err;
    }
  };

  const getToken = async () => {
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken(true);
    }
    return null;
  };

  const value = {
    user,
    dbUser,
    token,
    loading,
    signInWithGoogle,
    sendEmailLink,
    logout,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
